import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Lock, 
  ShieldCheck, 
  Server, 
  Database, 
  Cpu, 
  Key, 
  CheckCircle2, 
  AlertTriangle, 
  Terminal, 
  Globe2, 
  FileCheck2, 
  Mail, 
  ExternalLink,
  ChevronRight,
  Fingerprint,
  Radio,
  Layers,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import EditorialHero from '@/components/ui/EditorialHero';
import { Language } from '@/types';

interface SecurityPageProps {
  lang: Language;
}

export const SecurityPage: React.FC<SecurityPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const isRtl = isAr;

  const pillars = [
    {
      icon: Key,
      code: 'PILLAR 01 // CRYPTOGRAPHY',
      titleEn: 'End-to-End Cryptographic Invariance',
      titleAr: 'التشفير الشامل والحصانة الرقمية',
      descEn: 'All data in transit is protected via TLS 1.3 with Perfect Forward Secrecy. Persistent datasets, vector topologies, and token hashes are encrypted using AES-256-GCM envelope encryption with hardware security module (HSM) key isolation.',
      descAr: 'تشفير صارم لكافة البيانات أثناء النقل عبر بروتوكول TLS 1.3 مع سرية أمامية كاملة. وتشفير البيانات المتجهية ومفاتيح الرموز باستخدام خوارزمية AES-256-GCM مع عزل المفاتيح عبر وحدات الأمان المادية (HSM).'
    },
    {
      icon: Radio,
      code: 'PILLAR 02 // ZERO-EGRESS',
      titleEn: 'Zero-Egress Air-Gapped Deployment',
      titleAr: 'النشر المعزول شبكياً وانعدام التسريب',
      descEn: 'Engineered for national security, defense, and high-confidentiality land registry enclaves. VEXL packages fully offline, containerized runtimes capable of local spatial inference with zero outbound packet transmission to the internet.',
      descAr: 'مصمم خصيصاً للقطاعات السيادية والدفاعية وسجلات الأراضي الحساسة. تدعم VEXL حزم النشر المعزول شبكياً (Air-Gapped) القادرة على العمل محلياً دون إرسال أي حزم بيانات للخارج.'
    },
    {
      icon: Terminal,
      code: 'PILLAR 03 // RUNTIME SANDBOX',
      titleEn: 'Deterministic ArcPy & Spatial Sandboxing',
      titleAr: 'العزل البرمجي الحتمي لنصوص ArcPy',
      descEn: 'Generated ArcPy and GDAL scripts execute inside hardened, ephemeral Linux cgroup microVM sandboxes with strict memory quotas, CPU isolation, and disabled network sockets, preventing remote code execution vectors.',
      descAr: 'تُنفذ نصوص ArcPy وGDAL المعقدة داخل بيئات حاويات افتراضية معزولة (MicroVMs) بحصص ذاكرة صارمة، وانعدام لأي مقابس شبكية غير مصرح بها، لمنع أي ثغرات برمجية.'
    },
    {
      icon: Server,
      code: 'PILLAR 04 // CLOUD COMPLIANCE',
      titleEn: 'SOC 2 Type II & Sovereign Cloud Governance',
      titleAr: 'حوكمة السحابة والامتثال لمعايير SOC 2',
      descEn: 'Cloud-hosted nodes operate exclusively within tier-IV enterprise facilities backed by SOC 2 Type II, ISO/IEC 27001, and CSA STAR certifications. Automated continuous SAST/DAST pipelines audit every build.',
      descAr: 'تعمل الخوادم السحابية حصرياً في مراكز بيانات من المستوى الرابع (Tier-IV) معتمدة بشهادات SOC 2 Type II وISO 27001، مع فحص أمني مؤتمت دوري لكافة حزم التحديث البرمجية.'
    }
  ];

  const securityMatrix = [
    {
      featureEn: 'Customer Data Training',
      featureAr: 'التدريب على بيانات العميل',
      cloudBad: 'Custom geometries mined to train public foundation models',
      vexlGood: '0% Guaranteed — Contractually & architecturally blocked'
    },
    {
      featureEn: 'Coordinate & Polygon Egress',
      featureAr: 'تسريب الإحداثيات والمضلعات',
      cloudBad: 'Sent to overseas third-party LLM inference endpoints',
      vexlGood: 'Processed in RAM enclave; zero outbound telemetry'
    },
    {
      featureEn: 'Code Execution Engine',
      featureAr: 'بيئة تنفيذ الأكواد المكانية',
      cloudBad: 'Unrestricted remote interpreters with internet access',
      vexlGood: 'Hardened microVM sandbox with disabled networking'
    },
    {
      featureEn: 'Air-Gapped Sovereign Readiness',
      featureAr: 'الجاهزية للنشر المعزول (Air-Gap)',
      cloudBad: 'Unavailable — Requires constant cloud API uplink',
      vexlGood: 'Full offline air-gapped deployment packages supported'
    },
    {
      featureEn: 'Sovereign Intellectual Property',
      featureAr: 'تسجيل الملكية الفكرية',
      cloudBad: 'Unregistered generic wrappers',
      vexlGood: 'Official Software Copyright: EGIPA Deposit #5007'
    }
  ];

  return (
    <PageTransition>
      <SEO 
        title={isAr ? 'معمارية الأمان وسيادة البيانات المكانية | منظومة فكسل' : 'Security Architecture & Data Sovereignty | VEXL Ecosystem'}
        description={
          isAr 
            ? 'معمارية الأمان والسيادة المكانية لمنظومة فكسل: التشفير المؤسسي TLS 1.3 وAES-256، النشر المعزول شبكياً (Air-Gapped)، والعزل البرمجي لنصوص ArcPy.'
            : 'VEXL Enterprise Security Architecture: End-to-end encryption, Zero-Egress Air-Gapped readiness, and hardened ArcPy execution sandboxing.'
        }
        lang={lang}
      />

      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden selection:bg-[#0E93A3]/30 ${
          isRtl ? 'font-arabic text-right' : 'font-sans text-left'
        }`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 pt-20 md:pt-24 pb-24">
          
          {/* Canonical Two-Column Editorial Split Hero */}
          <EditorialHero
            title={isAr ? 'سيادة البيانات المكانية ومعمارية الأمان التشفيري المؤسسي' : 'Enterprise Data Sovereignty & Cryptographic Architecture'}
            description={
              isAr
                ? 'صُممت فكسل بمعايير دفاعية صارمة لحماية أصول البيانات الجيومكانية والخرائط السيادية. نضمن عزلاً حتمياً لكافة مسارات المعالجة، وانعداماً تاماً لتسريب الإحداثيات أو التدريب عليها.'
                : 'VEXL is engineered with defense-grade architectural boundaries to safeguard mission-critical geospatial datasets, municipal vectors, and national satellite telemetry.'
            }
            actions={[
              { 
                label: isAr ? 'مركز الأمان والموثوقية' : 'Trust & Safety Center', 
                href: '/safety', 
                variant: 'secondary' 
              },
              { 
                label: isAr ? 'الملكية الفكرية (EGIPA)' : 'IP Rights (EGIPA)', 
                href: '/intellectual-property', 
                variant: 'secondary' 
              }
            ]}
            isAr={isAr}
            className="mb-8"
          />

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
              <div className="p-4 rounded-xl bg-[#12161A]/80 border border-[#262C31]">
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">
                  {isAr ? 'بروتوكول النقل' : 'In-Transit Security'}
                </p>
                <p className="text-sm font-mono font-bold text-white">TLS 1.3 / PFS</p>
              </div>
              <div className="p-4 rounded-xl bg-[#12161A]/80 border border-[#262C31]">
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">
                  {isAr ? 'تشفير السكون' : 'At-Rest Standard'}
                </p>
                <p className="text-sm font-mono font-bold text-white">AES-256-GCM</p>
              </div>
              <div className="p-4 rounded-xl bg-[#12161A]/80 border border-[#262C31]">
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">
                  {isAr ? 'جاهزية العزل' : 'Air-Gap Readiness'}
                </p>
                <p className="text-sm font-mono font-bold text-[#0E93A3]">100% OFFLINE</p>
              </div>
              <div className="p-4 rounded-xl bg-[#12161A]/80 border border-[#262C31]">
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">
                  {isAr ? 'التدريب على البيانات' : 'Customer Training'}
                </p>
                <p className="text-sm font-mono font-bold text-emerald-400">0% (NEVER)</p>
              </div>
            </div>

          {/* Pillars of Security */}
          <div className="mb-16">
            <div className="mb-8">
              <p className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold mb-2">
                FOUR CORE PILLARS
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                {isAr ? 'الأعمدة الأربعة للأمان والسيادة المكانية' : 'The Four Architectural Security Pillars'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pillars.map((pillar, idx) => {
                const Icon = pillar.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl bg-[#12161A]/80 border border-[#262C31] hover:border-[#0E93A3]/40 transition-all backdrop-blur-sm relative overflow-hidden group"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] font-mono tracking-widest text-[#0E93A3] uppercase font-bold">
                        {pillar.code}
                      </span>
                      <div className="w-10 h-10 rounded-xl bg-[#0E93A3]/10 border border-[#0E93A3]/25 flex items-center justify-center text-[#0E93A3] group-hover:scale-105 transition-transform">
                        <Icon size={20} />
                      </div>
                    </div>

                    <h3 className="text-lg sm:text-xl font-bold text-white mb-3">
                      {isAr ? pillar.titleAr : pillar.titleEn}
                    </h3>

                    <p className="text-sm text-[#94A3B8] leading-relaxed">
                      {isAr ? pillar.descAr : pillar.descEn}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Security Comparison Matrix */}
          <div className="mb-16 p-6 sm:p-8 rounded-2xl bg-[#12161A]/60 border border-[#262C31]">
            <div className="mb-6">
              <span className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold mb-1 block">
                AUDITABLE COMPARISON
              </span>
              <h3 className="text-xl sm:text-2xl font-bold text-white">
                {isAr ? 'مقارنة معمارية الأمان: فكسل مقابل الأدوات السحابية العامة' : 'Security Comparison: VEXL vs Generic AI Wrappers'}
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm text-left border-collapse" dir={isRtl ? 'rtl' : 'ltr'}>
                <thead>
                  <tr className="border-b border-[#262C31] text-[#64748B] font-mono uppercase text-[11px]">
                    <th className="py-3 px-4">{isAr ? 'الخاصية الأمنية' : 'Security Capability'}</th>
                    <th className="py-3 px-4">{isAr ? 'المساعدات السحابية العامة' : 'Generic Cloud LLM Wrappers'}</th>
                    <th className="py-3 px-4 text-[#0E93A3]">{isAr ? 'منظومة فكسل السيادية' : 'VEXL Sovereign GeoAI'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#262C31]/50">
                  {securityMatrix.map((row, idx) => (
                    <tr key={idx} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-3.5 px-4 font-medium text-white">
                        {isAr ? row.featureAr : row.featureEn}
                      </td>
                      <td className="py-3.5 px-4 text-red-400/80">
                        {row.cloudBad}
                      </td>
                      <td className="py-3.5 px-4 text-emerald-400 font-semibold flex items-center gap-1.5">
                        <CheckCircle2 size={14} className="shrink-0 text-emerald-400" />
                        <span>{row.vexlGood}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Responsible Vulnerability Disclosure Section */}
          <div className="p-8 rounded-2xl bg-gradient-to-r from-[#12161A] to-[#0E93A3]/10 border border-[#262C31] flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0E93A3]/10 text-[#0E93A3] text-xs font-mono mb-3">
                <FileCheck2 size={13} />
                {isAr ? 'برنامج الإفصاح الأمني المسؤول' : 'RESPONSIBLE DISCLOSURE PROGRAM'}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {isAr ? 'الإبلاغ عن الثغرات الأمنية والتعاون مع الباحثين' : 'Security Research & Vulnerability Reporting'}
              </h3>
              <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
                {isAr
                  ? 'نقدر جهود الباحثين الأمنيين المستقلين. نلتزم باتفاقية استجابة وتدقيق خلال 48 ساعة لكافة التقارير الواردة عبر البريد الرسمي، مع تقديم مكافآت للمساهمات النوعية.'
                  : 'We value responsible security research. We commit to a 48-hour triage acknowledgment for all reported vulnerabilities via our official security channel.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0 w-full md:w-auto">
              <a
                href="mailto:vexl.gis@gmail.com?subject=[SECURITY%20DISCLOSURE]%20VEXL%20Vulnerability%20Report"
                className="px-5 py-3 rounded-xl bg-[#0E93A3] text-black font-bold text-xs sm:text-sm hover:bg-[#0bb2c6] transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(14,147,163,0.35)]"
              >
                <Mail size={16} />
                <span>vexl.gis@gmail.com</span>
              </a>
              <Link
                to="/safety"
                className="px-4 py-3 rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3] text-white text-xs sm:text-sm font-semibold transition-all text-center"
              >
                {isAr ? 'مركز الموثوقية والأمان' : 'Trust & Safety Center'}
              </Link>
            </div>
          </div>

          {/* Footer Legal Stamp */}
          <div className="mt-14 pt-8 border-t border-[#262C31]/70 text-center">
            <p className="text-[#64748B] text-xs font-mono">
              {isAr 
                ? 'معمارية أمان فكسل معتمدة ومطابقة للقانون المصري لحماية البيانات رقم 151 لسنة 2020 ومسجلة تحت رقم إيداع EGIPA #5007.' 
                : 'VEXL Security Architecture complies with Egyptian Law No. 151/2020 and is certified under EGIPA Software Deposit #5007.'}
            </p>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default SecurityPage;
